import React, { useEffect, useState } from 'react';
import { FieldProps } from '@rjsf/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

/**
 * ClusterMappingEnvironmentPicker
 * 
 * 自定义字段：根据选中的 system 动态加载可用的环境列表
 * 
 * 使用方式：
 * ```yaml
 * environment:
 *   title: 部署环境
 *   type: string
 *   ui:field: ClusterMappingEnvironmentPicker
 *   ui:options:
 *     systemField: system
 * ```
 */
export const ClusterMappingEnvironmentPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
  formContext,
}: FieldProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  
  const [environments, setEnvironments] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  // 从 formContext 或 uiSchema 获取 system 字段名
  const systemField = uiSchema?.['ui:options']?.systemField || 'system';
  const selectedSystem = formContext?.formData?.[systemField];

  useEffect(() => {
    if (!selectedSystem) {
      setEnvironments([]);
      return;
    }

    const loadEnvironments = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const backendUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();

        // 使用新的 API: /cluster-mapping/systems/:system
        const response = await fetch(
          `${backendUrl}/cluster-mapping/systems/${selectedSystem}`,
          {
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load environments: ${response.statusText}`);
        }

        const systemConfig = await response.json();
        
        if (!systemConfig || !systemConfig.environments) {
          setEnvironments([]);
          return;
        }

        // 获取已配置的环境列表
        const envList = Object.keys(systemConfig.environments).map(env => ({
          id: env,
          name: env.toUpperCase(),
        }));

        setEnvironments(envList);
        
        // 如果之前选中的环境在新的列表中不存在，清空选择
        if (formData && !envList.find(e => e.id === formData)) {
          onChange(undefined as any);
        }
      } catch (err) {
        console.error('Failed to load cluster mapping environments:', err);
        setError(err instanceof Error ? err.message : 'Failed to load environments');
      } finally {
        setLoading(false);
      }
    };

    loadEnvironments();
  }, [selectedSystem, discoveryApi, identityApi, formData, onChange]);

  // 如果没有选择 system，显示提示
  if (!selectedSystem) {
    return (
      <FormControl margin="normal" required={required} fullWidth disabled>
        <InputLabel htmlFor={idSchema.$id}>
          {schema.title || 'Environment'}
        </InputLabel>
        <Select id={idSchema.$id} value="">
          <MenuItem value="" disabled>
            请先选择系统
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title || 'Environment'}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && environments.length === 0 && (
          <MenuItem value="" disabled>
            该系统下无可用环境
          </MenuItem>
        )}
        {environments.map(env => (
          <MenuItem key={env.id} value={env.id}>
            {env.name}
          </MenuItem>
        ))}
      </Select>
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
    </FormControl>
  );
};
